import type { RouteLocationNormalized } from 'vue-router'

const STORAGE_KEY = 'tecnogest_route_visit_counts'

export interface VisitStat {
  path: string
  count: number
  title: string
  /** Texto pequeño debajo del título (p. ej. «proceso» en ficha de proceso). */
  subtitle?: string
}

interface StoredEntry {
  count: number
  title: string
  subtitle?: string
}

type StoredMap = Record<string, StoredEntry>

const SKIP_PATH_PREFIXES = ['/login']

/** Rutas que no deben acumular visitas (p. ej. el propio inicio). */
function shouldSkipRecording(to: RouteLocationNormalized): boolean {
  if (!to.path || to.path === '/') return true
  if (SKIP_PATH_PREFIXES.some((p) => to.path === p || to.path.startsWith(p + '/'))) return true
  return false
}

function routeTitle(to: RouteLocationNormalized): string {
  const n = to.name
  if (typeof n === 'string' && n.length) return n
  return to.path || '/'
}

function loadMap(): StoredMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as StoredMap
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function saveMap(map: StoredMap) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
}

/**
 * Clave en localStorage: path habitual; si hay incidencia abierta en modal,
 * path + ?incidencia=id (acceso directo al mismo estado).
 */
export function visitStorageKey(to: RouteLocationNormalized): string {
  const path = to.path
  if (path === '/organizacion/incidencias') {
    const raw = to.query.incidencia
    const id = Array.isArray(raw) ? raw[0] : raw
    if (typeof id === 'string' && id.trim() !== '') {
      return `${path}?incidencia=${encodeURIComponent(id.trim())}`
    }
  }
  return path
}

/** Misma clave que usa `visitStorageKey` al abrir una incidencia por id. */
export function incidenciaShortcutStorageKey(incidenciaId: number | string): string {
  return `/organizacion/incidencias?incidencia=${encodeURIComponent(String(incidenciaId).trim())}`
}

/** Incrementa el contador para la ruta visitada. */
export function recordRouteVisit(to: RouteLocationNormalized) {
  if (shouldSkipRecording(to)) return

  const key = visitStorageKey(to)
  const map = loadMap()
  const prev = map[key]
  const fallbackTitle = routeTitle(to)

  const next: StoredEntry = {
    count: (prev?.count ?? 0) + 1,
    title: (prev?.title ?? fallbackTitle) || key,
  }
  if (prev?.subtitle !== undefined) {
    next.subtitle = prev.subtitle
  }
  map[key] = next
  saveMap(map)
}

/** Ficha de proceso: `/organizacion/proceso/:id` */
export function isOrganizacionProcesoDetalle(path: string): boolean {
  return /^\/organizacion\/proceso\/[^/]+$/.test(path)
}

/** Actualiza título (y subtítulo) mostrado en accesos directos sin perder el contador. */
export function setShortcutDisplay(
  path: string,
  updates: { title: string; subtitle?: string }
) {
  const map = loadMap()
  const prev = map[path]
  if (!prev) {
    map[path] = {
      count: 1,
      title: updates.title,
      ...(updates.subtitle !== undefined ? { subtitle: updates.subtitle } : {}),
    }
  } else {
    map[path] = {
      ...prev,
      title: updates.title,
      ...(updates.subtitle !== undefined ? { subtitle: updates.subtitle } : {}),
    }
  }
  saveMap(map)
}

/** Rutas más visitadas, orden descendente por contador. */
export function getTopVisited(limit = 20): VisitStat[] {
  const map = loadMap()
  return Object.entries(map)
    .map(([path, v]) => ({
      path,
      count: v.count,
      title: v.title || path,
      ...(v.subtitle !== undefined ? { subtitle: v.subtitle } : {}),
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
}
